import { NotFoundMessage } from '@hendrx/core/common/components/messages/PageMessage/Messages/NotFoundMessage';
import { useGetLatestLearningActivitySnapshotIdQuery } from '@hendrx/core/learningActivity/queries/getLatestLearningActivitySnapshotId';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { LEARNING_ACTIVITY_PLAYER_PATH, PLENARY_PLAYER_PATH } from 'shared/common/consts/paths';
import { CustomLoadingOverlay } from 'shared/components/app/CustomLoadingOverlay';

import { createRouteGuard } from '@/app/_lib/guards/createRouteGuard';
import { allowFreeContentEmbed, anyOf, requireAuthenticated, requirePlenaryCookie } from '@/app/_lib/guards/primitives';

export const Route = createFileRoute('/player/$learningObjectDraftId')({
  // Also allow an anonymous 720 embed to reach the redirect hop (server stays authoritative).
  beforeLoad: createRouteGuard(anyOf(requireAuthenticated(), requirePlenaryCookie(), allowFreeContentEmbed())),
  component: RedirectPage
});

function RedirectPage() {
  const { learningObjectDraftId } = Route.useParams();
  const navigate = useNavigate();

  const {
    data: latestSnapshotId,
    isLoading,
    isError
  } = useGetLatestLearningActivitySnapshotIdQuery({
    learningActivityDraftId: learningObjectDraftId,
    publishedOnly: true
  });

  useEffect(() => {
    if (isLoading || isError) return;

    // Forward the raw query string verbatim. TanStack Router's navigate({ search })
    // would JSON-serialize objects and re-format the URL (producing artefacts like
    // `??` for complex search), which would corrupt the Infinity slxapi object on
    // the way to the destination route. Direct string concatenation preserves the
    // exact URL Infinity injected, which is what the API server later reads from
    // the Referer header to forward xAPI events to infinityevents.cet.ac.il.
    // Search param contract lives on the parent /player route (apps/hendrx/src/routes/player.tsx).
    const rawSearch = window.location.search;
    const base = latestSnapshotId?._id ? LEARNING_ACTIVITY_PLAYER_PATH : PLENARY_PLAYER_PATH;

    void navigate({
      to: `${base}/${learningObjectDraftId}${rawSearch}`,
      replace: true
    });
  }, [navigate, learningObjectDraftId, latestSnapshotId?._id, isLoading, isError]);

  if (isError) {
    return <NotFoundMessage />;
  }
  return <CustomLoadingOverlay visible />;
}
